/**
 * RTWO Native Currency Engine — Service Factory
 */

import { natvInternalEventBus } from '../events/natv-event-bus';
import {
  assetAdapterRegistry,
  eventBusAdapterRegistry,
  ledgerAdapterRegistry,
  protocolAdapterRegistry,
  storageAdapterRegistry,
  type AssetAdapterRegistry,
  type EventBusAdapterRegistry,
  type LedgerAdapterRegistry,
  type ProtocolAdapterRegistry,
  type StorageAdapterRegistry,
} from '../registry/adapter-registries';
import { createNATVRepositories, type NATVRepositories } from '../repositories/natv-repositories';
import { AllocationPolicyService } from './allocation-policy-service';
import { EconomicParameterService } from './economic-parameter-service';
import { EmissionScheduleService } from './emission-schedule-service';
import { FeePolicyService } from './fee-policy-service';
import { MonetaryPolicyService } from './monetary-policy-service';
import { NativeCurrencyEventPublisherService } from './native-currency-event-publisher-service';
import { NativeCurrencyReadinessService } from './native-currency-readiness-service';
import { NativeCurrencyRegistryService } from './native-currency-registry-service';
import { NativeCurrencyValidationService } from './native-currency-validation-service';
import { SupplyStateService } from './supply-state-service';
import type { NATVServiceContext } from './base-natv-service';

export interface NATVEngineServices {
  nativeCurrencyRegistry: NativeCurrencyRegistryService;
  monetaryPolicyService: MonetaryPolicyService;
  supplyStateService: SupplyStateService;
  allocationPolicyService: AllocationPolicyService;
  feePolicyService: FeePolicyService;
  emissionScheduleService: EmissionScheduleService;
  economicParameterService: EconomicParameterService;
  nativeCurrencyEventPublisher: NativeCurrencyEventPublisherService;
  nativeCurrencyValidationService: NativeCurrencyValidationService;
  nativeCurrencyReadinessService: NativeCurrencyReadinessService;
}

export function createNATVEngineServices(options?: {
  repositories?: NATVRepositories;
  ledgerRegistry?: LedgerAdapterRegistry;
  storageRegistry?: StorageAdapterRegistry;
  protocolRegistry?: ProtocolAdapterRegistry;
  assetRegistry?: AssetAdapterRegistry;
  eventBusRegistry?: EventBusAdapterRegistry;
  eventBus?: typeof natvInternalEventBus;
}): {
  repositories: NATVRepositories;
  ledgerRegistry: LedgerAdapterRegistry;
  storageRegistry: StorageAdapterRegistry;
  protocolRegistry: ProtocolAdapterRegistry;
  assetRegistry: AssetAdapterRegistry;
  eventBusRegistry: EventBusAdapterRegistry;
  services: NATVEngineServices;
  context: NATVServiceContext;
} {
  const repositories = options?.repositories ?? createNATVRepositories();
  const ledgerRegistry = options?.ledgerRegistry ?? ledgerAdapterRegistry;
  const storageRegistry = options?.storageRegistry ?? storageAdapterRegistry;
  const protocolRegistry = options?.protocolRegistry ?? protocolAdapterRegistry;
  const assetRegistry = options?.assetRegistry ?? assetAdapterRegistry;
  const eventBusRegistry = options?.eventBusRegistry ?? eventBusAdapterRegistry;
  const context: NATVServiceContext = {
    eventBus: options?.eventBus ?? natvInternalEventBus,
  };

  const services: NATVEngineServices = {
    nativeCurrencyRegistry: new NativeCurrencyRegistryService(
      context,
      repositories,
      protocolRegistry,
      assetRegistry
    ),
    monetaryPolicyService: new MonetaryPolicyService(context, repositories, ledgerRegistry),
    supplyStateService: new SupplyStateService(context, repositories, ledgerRegistry),
    allocationPolicyService: new AllocationPolicyService(context, repositories, ledgerRegistry),
    feePolicyService: new FeePolicyService(context, repositories, protocolRegistry),
    emissionScheduleService: new EmissionScheduleService(context, repositories, ledgerRegistry),
    economicParameterService: new EconomicParameterService(context),
    nativeCurrencyEventPublisher: new NativeCurrencyEventPublisherService(
      context,
      repositories,
      eventBusRegistry
    ),
    nativeCurrencyValidationService: new NativeCurrencyValidationService(
      context,
      ledgerRegistry,
      protocolRegistry
    ),
    nativeCurrencyReadinessService: new NativeCurrencyReadinessService(context),
  };

  return {
    repositories,
    ledgerRegistry,
    storageRegistry,
    protocolRegistry,
    assetRegistry,
    eventBusRegistry,
    services,
    context,
  };
}
